import AppBackdrop from '@/components/AppBackdrop';
import AppModal from '@/components/AppModal';
import classes from 'classnames';
import { Transition } from 'react-transition-group';
import classNames from './LevelTestLogOut.module.scss';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/store';
import userThunks from '@/store/thunks/user';
import { authActions, authSelector } from '@/store/reducers/auth';
import { appSettingsActions } from '@/store/reducers/settings';
import { useNavigate } from 'react-router-dom';
import { levelTestActions, levelTestSelector } from '@/store/reducers/level-test';
import { gameActions } from '@/store/reducers/game';
import UseScreenSize from '@/hooks/UseScreenSize';
import SVG from 'react-inlinesvg';
import close from '@/assets/svg/close.svg';
import coinBroken from '@/assets/svg/coin-broken.svg';
import TheButton from '@/components/TheButton/TheButton';

type LevelTestLogOutProps = {
  showLogoutModal: boolean;
  setShowLogoutModal: (show: boolean) => void;
};

const LevelTestLogOut = ({ showLogoutModal, setShowLogoutModal }: LevelTestLogOutProps) => {
  const { t } = useTranslation();
  const { isMobile, isDesktop } = UseScreenSize();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(authSelector);
  const { activity, questionIndex, answers } = useAppSelector(levelTestSelector);
  const isTeacher = user?.metadata.role === 'teacher';
  const navigate = useNavigate();

  const answeredCount = answers?.length ?? 0;

  const logOut = useCallback(() => {
    dispatch(userThunks.signOut());
    dispatch(authActions.signOut());
    dispatch(levelTestActions.isLogOut());
    dispatch(appSettingsActions.clearAppSettings());
  }, [dispatch]);

  const handleSecondaryAction = useCallback(async () => {
    if (isTeacher) {
      dispatch(gameActions.setIsLevelTest(false));
      dispatch(levelTestActions.reset());
      dispatch(levelTestActions.setIsFinishLevelTest(false));
      navigate('/dashboard/games');
      return;
    }

    logOut();
  }, [dispatch, isTeacher, navigate, logOut]);

  useEffect(() => {
    if (showLogoutModal && answeredCount === 0) {
      setShowLogoutModal(false);
      handleSecondaryAction();
    }
  }, [showLogoutModal, answeredCount, handleSecondaryAction]);

  if (showLogoutModal && answeredCount === 0) {
    return null;
  }

  return (
    <Transition in={showLogoutModal} timeout={400} mountOnEnter unmountOnExit>
      {state => (
        <>
          <AppBackdrop
            className={classes(classNames.backdrop, `backdrop-${state}`)}
            emitClose={() => setShowLogoutModal(false)}
          />
          <AppModal>
            <div
              className={classes(classNames.popUpPause, {
                [`fade-${state}`]: !isMobile,
                [`slide-vertical-${state}`]: isMobile,
              })}
            >
              <div className={classNames.close} onClick={() => setShowLogoutModal(false)}>
                <SVG src={close} />
              </div>
              <div className={classNames.title}>
                <h3>{t('level_test')}</h3>
                <h3>{t('paussed')}</h3>
              </div>
              <div className={classNames.details}>
                <SVG src={coinBroken} />
                <span className={classNames.keepPlaying}>{t('keepPlaying')}</span>
                <span className={classNames.description}>
                  {t('levelTestPausedDescription', { count: Math.max(answeredCount, 1) })}
                </span>
              </div>
              <div className={classNames.buttons}>
                <TheButton
                  className={isDesktop ? 'flex-1' : 'w-full'}
                  text={t('resume')}
                  emitOnClick={() => setShowLogoutModal(false)}
                  shouldRecolor
                />
                <TheButton
                  text={isTeacher ? t('backHome') : t('sign_out')}
                  className={isDesktop ? 'flex-1' : 'w-full'}
                  plain
                  emitOnClick={handleSecondaryAction}
                />
              </div>
            </div>
          </AppModal>
        </>
      )}
    </Transition>
  );
};

export default LevelTestLogOut;
